/**
 * KPI Scorecard Table Component
 *
 * Spreadsheet-style table view of KPIs with inline editing.
 * Displays KPIs as rows and periods as columns for fast data entry.
 * Supports drag-and-drop reordering of KPIs within categories.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle, AlertCircle, Target, GripVertical, SlidersHorizontal, Search, X } from 'lucide-react';
import { OwnerAvatar } from '@/shared/components/OwnerAvatar';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { KpiValueCell, formatDisplayValue } from './KpiValueCell';
import { KPI_CATEGORIES } from '../types';
import type { ScorecardResponse, ScorecardKpi, ScorecardPeriod, KpiCategory } from '../types';

export type KpiStatusFilter = 'all' | 'on_track' | 'off_track' | 'no_data';

const COLUMN_VISIBILITY_KEY = 'kpi-scorecard-columns';

type ColumnKey = 'category' | 'team' | 'status' | 'goal' | 'avg' | 'total';

const ALL_COLUMNS: { key: ColumnKey; label: string }[] = [
  { key: 'category', label: 'Category' },
  { key: 'team', label: 'Team' },
  { key: 'status', label: 'Status' },
  { key: 'goal', label: 'Goal' },
  { key: 'avg', label: 'Avg' },
  { key: 'total', label: 'Total' },
];

function loadColumnVisibility(): Set<ColumnKey> {
  try {
    const stored = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (stored) return new Set(JSON.parse(stored));
  } catch { /* ignore */ }
  return new Set(['category', 'team', 'status', 'goal', 'avg', 'total']);
}

interface TeamMap {
  [id: string]: string; // team ID → team name
}

interface KpiScorecardProps {
  scorecard: ScorecardResponse;
  teamMap?: TeamMap;
  onSaveValue: (kpiId: string, datetime: string, value: number, notes?: string) => Promise<void>;
  onClearValue?: (kpiId: string, periodStart: string, periodEnd: string) => Promise<void>;
  savingCells?: Set<string>;
  onReorder?: (category: string, kpiIds: string[]) => void;
  onEditKpi?: (kpiId: string) => void;
  highlightedKpiId?: string | null;
  // In-header filters
  searchText?: string;
  onSearchTextChange?: (value: string) => void;
  selectedCategory?: KpiCategory | '_all';
  onSelectedCategoryChange?: (value: KpiCategory | '_all') => void;
  selectedTeamId?: string | null;
  onSelectedTeamIdChange?: (value: string | null) => void;
  teams?: { id: string; name: string }[];
  selectedStatus?: KpiStatusFilter;
  onSelectedStatusChange?: (value: KpiStatusFilter) => void;
}

function formatGoal(kpi: ScorecardKpi): string | null {
  if (kpi.goal_value !== null) {
    return formatDisplayValue(kpi.goal_value, kpi.goal_unit, kpi.number_format);
  }
  if (kpi.goal_min !== null && kpi.goal_max !== null) {
    const min = formatDisplayValue(kpi.goal_min, null, kpi.number_format);
    const max = formatDisplayValue(kpi.goal_max, kpi.goal_unit, kpi.number_format);
    return `${min} - ${max}`;
  }
  return null;
}

function OnTrackIndicator({ onTrack }: { onTrack: boolean | null }) {
  if (onTrack === null) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex items-center justify-center h-5 w-5 rounded-full shrink-0',
              onTrack ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
            )}
          >
            {onTrack ? (
              <CheckCircle className="h-3 w-3" />
            ) : (
              <AlertCircle className="h-3 w-3" />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>{onTrack ? 'On Track' : 'Off Track'}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// --- Sortable KPI Row ---

interface SortableKpiRowProps {
  kpi: ScorecardKpi;
  categoryName: string;
  teamMap: TeamMap;
  visibleColumns: Set<ColumnKey>;
  periods: ScorecardPeriod[];
  savingCells: Set<string>;
  focusedCell: { kpiId: string; periodIdx: number } | null;
  unlockedCells: Set<string>;
  onSave: (kpiId: string, datetime: string, value: number, notes?: string) => Promise<void>;
  onClear?: (kpiId: string, periodStart: string, periodEnd: string) => Promise<void>;
  onNavigate: (kpiId: string, periodIdx: number, direction: 'up' | 'down' | 'left' | 'right') => void;
  onUnlockCell: (cellKey: string) => void;
  onEditKpi?: (kpiId: string) => void;
  highlightedKpiId?: string | null;
  kpiColWidth: number;
}

function SortableKpiRow({
  kpi,
  categoryName,
  teamMap,
  visibleColumns,
  periods,
  savingCells,
  focusedCell,
  unlockedCells,
  onSave,
  onClear,
  onNavigate,
  onUnlockCell,
  onEditKpi,
  highlightedKpiId,
  kpiColWidth,
}: SortableKpiRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: kpi.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  const isHighlighted = highlightedKpiId === kpi.id;

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={cn(isHighlighted && 'animate-pulse bg-primary/5')}
    >
      {/* KPI Name with drag handle */}
      <TableCell
        className="sticky left-0 bg-card font-medium cursor-pointer hover:bg-muted/30 transition-colors"
        style={{ width: kpiColWidth, minWidth: kpiColWidth, maxWidth: kpiColWidth }}
        onClick={() => onEditKpi?.(kpi.id)}
      >
        <div className="flex items-start gap-1.5">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none shrink-0 mt-0.5 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
          >
            <GripVertical className="h-4 w-4" />
          </div>
          <div className="flex flex-col min-w-0 flex-1">
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate border-b border-dashed border-transparent hover:border-muted-foreground/40">
                    {kpi.name}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <div className="space-y-1 text-xs">
                    {kpi.description && <p>{kpi.description}</p>}
                    {kpi.owner_name && <p className="text-muted-foreground">Owner: {kpi.owner_name}</p>}
                    {formatGoal(kpi) && <p className="text-muted-foreground">Goal: {formatGoal(kpi)}</p>}
                    <p className="text-muted-foreground/60 italic pt-1">Click to edit</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {kpi.owner_name && (
              <span className="text-xs text-muted-foreground">{kpi.owner_name}</span>
            )}
            {kpi.indicator_type && (
              <Badge variant="outline" className={cn(
                "text-[10px] px-1 py-0 h-4 font-normal",
                kpi.indicator_type === 'leading' ? 'text-blue-600 border-blue-200' : 'text-amber-600 border-amber-200'
              )}>
                {kpi.indicator_type === 'leading' ? 'Lead' : 'Lag'}
              </Badge>
            )}
          </div>
        </div>
      </TableCell>

      {/* Category */}
      {visibleColumns.has('category') && (
        <TableCell className="text-xs text-muted-foreground capitalize">
          {categoryName}
        </TableCell>
      )}

      {/* Team */}
      {visibleColumns.has('team') && (
        <TableCell className="text-xs text-muted-foreground">
          {kpi.team_ids?.map((id) => teamMap[id] || '').filter(Boolean).join(', ') || '—'}
        </TableCell>
      )}

      {/* On Track Status */}
      {visibleColumns.has('status') && (
        <TableCell className="text-center">
          <div className="flex justify-center">
            <OnTrackIndicator onTrack={kpi.on_track} />
          </div>
        </TableCell>
      )}

      {/* Goal */}
      {visibleColumns.has('goal') && (
        <TableCell className="text-right">
          {kpi.show_goal && formatGoal(kpi) && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground whitespace-nowrap">
                    <Target className="h-3 w-3" />
                    <span>{formatGoal(kpi)}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Goal</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </TableCell>
      )}

      {/* Average */}
      {visibleColumns.has('avg') && (
        <TableCell className="text-right">
          {kpi.show_average && kpi.average !== null && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatDisplayValue(kpi.average, kpi.goal_unit, kpi.number_format)}
            </span>
          )}
        </TableCell>
      )}

      {/* Total */}
      {visibleColumns.has('total') && (
        <TableCell className="text-right">
          {kpi.show_total && kpi.total !== null && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatDisplayValue(kpi.total, kpi.goal_unit, kpi.number_format)}
            </span>
          )}
        </TableCell>
      )}

      {/* Period Value Cells */}
      {periods.map((period, periodIdx) => {
        const periodValue = kpi.values.find((v) => v.period_start === period.start) ?? null;
        const cellKey = `${kpi.id}-${period.start}`;
        const isSaving = savingCells.has(cellKey);
        const isFocused =
          focusedCell?.kpiId === kpi.id && focusedCell?.periodIdx === periodIdx;
        const isUnlocked = unlockedCells.has(cellKey);

        return (
          <TableCell
            key={period.start}
            className={cn('p-1', isFocused && 'ring-2 ring-primary ring-inset')}
          >
            <KpiValueCell
              kpi={kpi}
              periodValue={periodValue}
              periodStart={period.start}
              periodEnd={period.end}
              onSave={onSave}
              onClear={onClear}
              isLoading={isSaving}
              isUnlocked={isUnlocked}
              onUnlock={() => onUnlockCell(cellKey)}
              onNavigate={(direction) => onNavigate(kpi.id, periodIdx, direction)}
            />
          </TableCell>
        );
      })}
    </TableRow>
  );
}

// --- Main Component ---

const KPI_COL_WIDTH_KEY = 'kpi-scorecard-kpi-col-width';
const KPI_COL_MIN = 160;
const KPI_COL_MAX = 600;

function loadKpiColWidth(): number {
  try {
    const stored = localStorage.getItem(KPI_COL_WIDTH_KEY);
    if (stored) {
      const n = parseInt(stored, 10);
      if (!Number.isNaN(n) && n >= KPI_COL_MIN && n <= KPI_COL_MAX) return n;
    }
  } catch { /* ignore */ }
  return 220;
}

export function KpiScorecard({
  scorecard,
  teamMap = {},
  onSaveValue,
  onClearValue,
  savingCells = new Set(),
  onReorder,
  onEditKpi,
  highlightedKpiId,
  searchText,
  onSearchTextChange,
  selectedCategory,
  onSelectedCategoryChange,
  selectedTeamId,
  onSelectedTeamIdChange,
  teams = [],
  selectedStatus,
  onSelectedStatusChange,
}: KpiScorecardProps) {
  const tableRef = useRef<HTMLTableElement>(null);
  const [focusedCell, setFocusedCell] = useState<{ kpiId: string; periodIdx: number } | null>(null);
  const [unlockedCells, setUnlockedCells] = useState<Set<string>>(new Set());
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(loadColumnVisibility);
  const [kpiColWidth, setKpiColWidth] = useState<number>(loadKpiColWidth);
  const kpiColResizing = useRef(false);

  const handleKpiColResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    kpiColResizing.current = true;
    const startX = e.clientX;
    const startWidth = kpiColWidth;
    const onMove = (ev: MouseEvent) => {
      if (!kpiColResizing.current) return;
      const delta = ev.clientX - startX;
      const next = Math.min(Math.max(startWidth + delta, KPI_COL_MIN), KPI_COL_MAX);
      setKpiColWidth(next);
    };
    const onUp = () => {
      kpiColResizing.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setKpiColWidth((w) => { localStorage.setItem(KPI_COL_WIDTH_KEY, String(w)); return w; });
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [kpiColWidth]);

  // Sync column visibility when toggled from the toolbar ColumnVisibilityToggle
  useEffect(() => {
    const handler = () => setVisibleColumns(loadColumnVisibility());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);



  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  // Handle unlocking a past period cell for editing
  const handleUnlockCell = useCallback((cellKey: string) => {
    setUnlockedCells((prev) => new Set(prev).add(cellKey));
  }, []);

  // Flatten all KPIs from all categories (with their category name attached for rendering)
  const flatKpis = scorecard.categories.flatMap((cat) =>
    cat.kpis.map((kpi) => ({ kpi, categoryName: cat.name }))
  );
  const allKpis = flatKpis.map((f) => f.kpi);
  const periods = scorecard.periods;

  // Group KPIs by owner (first-seen owner order; unassigned last)
  const ownerGroups = (() => {
    const groups = new Map<string, { ownerName: string; ownerId: string | null; items: typeof flatKpis }>();
    for (const item of flatKpis) {
      const key = item.kpi.owner_id ?? '__unassigned__';
      const name = item.kpi.owner_name ?? 'Unassigned';
      if (!groups.has(key)) {
        groups.set(key, { ownerName: name, ownerId: item.kpi.owner_id ?? null, items: [] });
      }
      groups.get(key)!.items.push(item);
    }
    // Move unassigned to end
    const entries = Array.from(groups.entries());
    const unassigned = entries.filter(([k]) => k === '__unassigned__');
    const assigned = entries.filter(([k]) => k !== '__unassigned__');
    return [...assigned, ...unassigned];
  })();

  // Total columns for section-header colspan
  const totalColumnCount = 1 + visibleColumns.size + periods.length;

  // Handle cell navigation
  const handleNavigate = useCallback(
    (kpiId: string, periodIdx: number, direction: 'up' | 'down' | 'left' | 'right') => {
      const kpiIndex = allKpis.findIndex((k) => k.id === kpiId);
      if (kpiIndex === -1) return;

      let newKpiIndex = kpiIndex;
      let newPeriodIdx = periodIdx;

      switch (direction) {
        case 'up':
          newKpiIndex = Math.max(0, kpiIndex - 1);
          break;
        case 'down':
          newKpiIndex = Math.min(allKpis.length - 1, kpiIndex + 1);
          break;
        case 'left':
          newPeriodIdx = Math.max(0, periodIdx - 1);
          break;
        case 'right':
          newPeriodIdx = Math.min(periods.length - 1, periodIdx + 1);
          break;
      }

      const newKpi = allKpis[newKpiIndex];
      if (newKpi) {
        setFocusedCell({ kpiId: newKpi.id, periodIdx: newPeriodIdx });
      }
    },
    [allKpis, periods]
  );

  const handleSave = useCallback(
    async (kpiId: string, datetime: string, value: number, notes?: string) => {
      await onSaveValue(kpiId, datetime, value, notes);
    },
    [onSaveValue]
  );

  const handleClear = useCallback(
    async (kpiId: string, periodStart: string, periodEnd: string) => {
      if (!onClearValue) return;
      await onClearValue(kpiId, periodStart, periodEnd);
    },
    [onClearValue]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !onReorder) return;

      const flatIds = allKpis.map((k) => k.id);
      const oldIndex = flatIds.indexOf(active.id as string);
      const newIndex = flatIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      const nextIds = arrayMove(flatIds, oldIndex, newIndex);
      onReorder('', nextIds);
    },
    [onReorder, allKpis]
  );

  if (allKpis.length === 0) {
    return null;
  }

  return (
    <div className="h-full">
      <div className="rounded-lg border bg-card overflow-auto h-full [&>div]:overflow-visible">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <Table ref={tableRef}>
          <TableHeader>
            {/* Filter row (pinned on top) */}
            <TableRow className="bg-muted hover:bg-muted">
              <TableHead
                className="sticky top-0 left-0 bg-muted z-40 py-1.5"
                style={{ width: kpiColWidth, minWidth: kpiColWidth, maxWidth: kpiColWidth }}
              >
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    value={searchText ?? ''}
                    onChange={(e) => onSearchTextChange?.(e.target.value)}
                    placeholder="Search KPI..."
                    className="h-7 pl-7 pr-7 text-xs"
                  />
                  {searchText && (
                    <button
                      onClick={() => onSearchTextChange?.('')}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Clear search"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </TableHead>
              {visibleColumns.has('category') && (
                <TableHead className="sticky top-0 bg-muted z-30 py-1.5">
                  <Select
                    value={selectedCategory ?? '_all'}
                    onValueChange={(v) => onSelectedCategoryChange?.(v as KpiCategory | '_all')}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All</SelectItem>
                      {KPI_CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableHead>
              )}
              {visibleColumns.has('team') && (
                <TableHead className="sticky top-0 bg-muted z-30 py-1.5">
                  <Select
                    value={selectedTeamId ?? '_all'}
                    onValueChange={(v) => onSelectedTeamIdChange?.(v === '_all' ? null : v)}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All</SelectItem>
                      {teams.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableHead>
              )}
              {visibleColumns.has('status') && (
                <TableHead className="sticky top-0 bg-muted z-30 py-1.5">
                  <Select
                    value={selectedStatus ?? 'all'}
                    onValueChange={(v) => onSelectedStatusChange?.(v as KpiStatusFilter)}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="on_track">On Track</SelectItem>
                      <SelectItem value="off_track">Off Track</SelectItem>
                      <SelectItem value="no_data">No Data</SelectItem>
                    </SelectContent>
                  </Select>
                </TableHead>
              )}
              {visibleColumns.has('goal') && <TableHead className="sticky top-0 bg-muted z-30" />}
              {visibleColumns.has('avg') && <TableHead className="sticky top-0 bg-muted z-30" />}
              {visibleColumns.has('total') && <TableHead className="sticky top-0 bg-muted z-30" />}
              {periods.map((period) => (
                <TableHead key={`f-${period.start}`} className="sticky top-0 bg-muted z-30" />
              ))}
            </TableRow>
            {/* Column labels row */}
            <TableRow className="hover:bg-transparent">
              <TableHead
                className="sticky top-10 left-0 bg-white z-40"
                style={{ width: kpiColWidth, minWidth: kpiColWidth, maxWidth: kpiColWidth, position: 'sticky' }}
              >
                <div className="relative h-full">
                  KPI
                  <div
                    onMouseDown={handleKpiColResize}
                    className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60 select-none"
                    aria-label="Resize KPI column"
                  />
                </div>
              </TableHead>
              {visibleColumns.has('category') && <TableHead className="sticky top-10 w-[100px] bg-white z-30">Category</TableHead>}
              {visibleColumns.has('team') && <TableHead className="sticky top-10 w-[140px] bg-white z-30">Team</TableHead>}
              {visibleColumns.has('status') && <TableHead className="sticky top-10 w-[120px] text-center bg-white z-30">Status</TableHead>}
              {visibleColumns.has('goal') && <TableHead className="sticky top-10 w-[110px] min-w-[110px] text-right bg-white z-30">Goal</TableHead>}
              {visibleColumns.has('avg') && <TableHead className="sticky top-10 w-[110px] min-w-[110px] text-right bg-white z-30">Avg</TableHead>}
              {visibleColumns.has('total') && <TableHead className="sticky top-10 w-[110px] min-w-[110px] text-right bg-white z-30">Total</TableHead>}
              {periods.map((period) => {
                const periodEndDate = new Date(period.end);
                const now = new Date();
                const isPastPeriod = now > periodEndDate;

                return (
                  <TableHead
                    key={period.start}
                    className={cn(
                      'sticky top-10 text-right min-w-[110px] z-30 bg-white',
                      !isPastPeriod && 'text-primary'
                    )}
                  >
                    <div className="flex flex-col items-end">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{period.label}</span>
                        {!isPastPeriod && (
                          <span className="text-[10px] bg-primary/10 text-primary px-1 rounded">
                            Edit
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground font-normal">
                        {period.end}
                      </span>
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            <SortableContext
              items={allKpis.map((k) => k.id)}
              strategy={verticalListSortingStrategy}
            >
              {ownerGroups.flatMap(([ownerKey, group]) => [
                <TableRow key={`owner-${ownerKey}`} className="hover:bg-muted/40 bg-muted/20">
                  <TableCell
                    colSpan={totalColumnCount}
                    className="sticky left-0 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    <div className="flex items-center gap-1.5">
                      <OwnerAvatar
                        ownerId={group.ownerId}
                        name={group.ownerName}
                        className="h-5 w-5"
                        fallbackClassName="text-[9px]"
                      />
                      <span>{group.ownerName}</span>
                      <span className="font-normal normal-case tracking-normal text-muted-foreground/70">
                        · {group.items.length} KPI{group.items.length === 1 ? '' : 's'}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>,
                ...group.items.map(({ kpi, categoryName }) => (
                  <SortableKpiRow
                    key={kpi.id}
                    kpi={kpi}
                    categoryName={categoryName}
                    teamMap={teamMap}
                    visibleColumns={visibleColumns}
                    periods={periods}
                    savingCells={savingCells}
                    focusedCell={focusedCell}
                    unlockedCells={unlockedCells}
                    onSave={handleSave}
                    onClear={onClearValue ? handleClear : undefined}
                    onNavigate={handleNavigate}
                    onUnlockCell={handleUnlockCell}
                    onEditKpi={onEditKpi}
                    highlightedKpiId={highlightedKpiId}
                    kpiColWidth={kpiColWidth}
                  />
                )),
              ])}
            </SortableContext>
          </TableBody>
        </Table>
      </DndContext>
    </div>
    </div>
  );
}

/**
 * Column visibility toggle button — render in the toolbar alongside other controls.
 * Uses the same localStorage key as KpiScorecard for shared state.
 */
export function ColumnVisibilityToggle() {
  const [visible, setVisible] = useState<Set<ColumnKey>>(loadColumnVisibility);

  const toggle = useCallback((key: ColumnKey) => {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify([...next]));
      window.dispatchEvent(new Event('storage'));
      return next;
    });
  }, []);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <SlidersHorizontal className="h-4 w-4 mr-1" />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-2">
        {ALL_COLUMNS.map((col) => (
          <label key={col.key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer">
            <Checkbox
              checked={visible.has(col.key)}
              onCheckedChange={() => toggle(col.key)}
            />
            <span className="text-sm">{col.label}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function KpiScorecardSkeleton({ periodCount = 4 }: { periodCount?: number }) {
  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="min-w-[200px]">
              <Skeleton className="h-4 w-24" />
            </TableHead>
            <TableHead className="w-[100px]">
              <Skeleton className="h-4 w-16" />
            </TableHead>
            <TableHead className="w-[80px]">
              <Skeleton className="h-4 w-12 mx-auto" />
            </TableHead>
            <TableHead className="w-[100px]">
              <Skeleton className="h-4 w-16 ml-auto" />
            </TableHead>
            <TableHead className="w-[100px]">
              <Skeleton className="h-4 w-12 ml-auto" />
            </TableHead>
            <TableHead className="w-[100px]">
              <Skeleton className="h-4 w-12 ml-auto" />
            </TableHead>
            {Array.from({ length: periodCount }).map((_, i) => (
              <TableHead key={i} className="min-w-[100px]">
                <div className="flex flex-col items-end gap-1">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 6 }).map((_, rowIdx) => (
            <TableRow key={rowIdx}>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-16" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-5 rounded-full mx-auto" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-12 ml-auto" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-10 ml-auto" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-10 ml-auto" />
              </TableCell>
              {Array.from({ length: periodCount }).map((_, colIdx) => (
                <TableCell key={colIdx}>
                  <Skeleton className="h-10 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
